import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';
import { Button } from './ui';
import { usePublicShippingRates } from '../hooks/useCheckoutApi';
import MediaPlaceholder from './MediaPlaceholder';
import { MinusIcon, PackageIcon, PlusIcon, TrashIcon, XIcon } from './ui/icons';

const CartDrawer = () => {
  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    removeFromCart,
    updateQuantity,
    getCartTotal,
    getRemainingForFreeShipping,
    getCartItemKey,
  } = useCart();

  const { language } = useLanguage();
  const { data: shippingRatesData } = usePublicShippingRates();
  const [brokenImages, setBrokenImages] = useState({});

  const handleClose = () => setIsCartOpen(false);

  if (!isCartOpen) return null;

  const subtotal = getCartTotal();
  const freeShippingThreshold = shippingRatesData?.freeShippingThreshold || 70;
  const hasCountrySpecificRates = (shippingRatesData?.rates?.length || 0) > 0;
  const isFreeShipping = subtotal >= freeShippingThreshold;
  const shouldShowPendingShipping = !isFreeShipping && hasCountrySpecificRates;
  const shipping = isFreeShipping
    ? 0
    : shouldShowPendingShipping
      ? null
      : Number(shippingRatesData?.defaultPrice ?? 5);
  const remaining = getRemainingForFreeShipping(subtotal, freeShippingThreshold);
  const total = subtotal + (Number.isFinite(shipping) ? shipping : 0);

  // Check if cart has model products
  const hasModelProducts = cartItems.some(item => item.category === 'Makete' || item.category === 'Models');

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 transition-opacity"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-dark border-l border-dark-border z-50 flex flex-col animate-slide-in-right shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-border bg-dark-lighter">
          <h2 className="text-2xl font-serif text-light font-bold">
            {t("shoppingCart", language)}
          </h2>
          <button
            onClick={handleClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg text-light-muted hover:text-light hover:bg-dark-lighter transition-colors"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Free shipping progress - Simplified */}
        {remaining > 0 && cartItems.length > 0 && (
          <div className="px-6 py-4 bg-bronze/5 border-b border-bronze/20">
            <p className="text-sm text-bronze mb-2 font-medium">
              {t("addMoreForFreeShipping", language, {
                amount: remaining.toFixed(2),
              })}
            </p>
            <div className="w-full bg-dark-border rounded-full h-1.5">
              <div
                className="bg-gradient-to-r from-bronze to-gold h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${Math.min((subtotal / freeShippingThreshold) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Model Accessories Notice - Simplified */}
        {hasModelProducts && (
          <div className="px-6 py-3 bg-bronze/5 border-b border-bronze/20">
            <p className="text-xs text-light-secondary">
              {t("modelAccessoriesNotice", language)}
              <br />
              <Link
                to="/shop?category=Model%20Accessories"
                onClick={handleClose}
                className="text-bronze hover:text-gold font-semibold"
              >
                {t("viewAccessories", language)}
              </Link>
            </p>
          </div>
        )}

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <svg
                className="w-20 h-20 text-light-muted/30 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                />
              </svg>
              <p className="text-light-muted text-lg mb-6">
                {t("yourCartIsEmpty", language)}
              </p>
              <Button variant="primary" onClick={handleClose}>
                {t("continueShopping", language)}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {cartItems.map((item) => {
                const itemKey = getCartItemKey(item);
                const primaryImage = item.images?.[0] || item.image || '';
                const showImage = Boolean(primaryImage) && !brokenImages[itemKey];

                return (
                  <div
                    key={itemKey}
                    className="p-3 rounded-lg bg-dark-lighter border border-dark-border hover:border-bronze/30 transition-colors"
                  >
                    <div className="flex items-stretch gap-3">
                      <div className="w-20 h-20 rounded-md overflow-hidden border border-dark-border bg-dark flex-shrink-0">
                        {showImage ? (
                          <img
                            src={primaryImage}
                            alt={item.name}
                            className="w-full h-full object-cover"
                            onError={() => {
                              setBrokenImages((prev) => ({ ...prev, [itemKey]: true }));
                            }}
                          />
                        ) : (
                          <MediaPlaceholder icon={PackageIcon} className="w-full h-full rounded-md" />
                        )}
                      </div>

                      <div className="relative flex-1 min-w-0 self-stretch h-20">
                        <h3
                          className="absolute left-0 right-10 top-0 text-light text-sm font-medium leading-tight text-left truncate"
                          title={item.name}
                        >
                          {item.name}
                        </h3>
                        <button
                          onClick={() => removeFromCart(itemKey)}
                          className="absolute right-0 top-0 text-light-muted hover:text-red-500 transition-colors w-8 h-8 flex items-center justify-center"
                          aria-label={t('delete', language)}
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>

                        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3">
                          <div className="flex flex-col">
                            {Number.isFinite(item.originalPrice) && item.originalPrice > item.price && (
                              <span className="text-[10px] text-light-muted line-through">
                                €{item.originalPrice.toFixed(2)}
                              </span>
                            )}
                            <p className="text-gold font-bold text-sm leading-none">
                              €{item.price.toFixed(2)}
                            </p>
                          </div>

                          <div className="inline-flex items-center rounded-lg border border-dark-border bg-dark overflow-hidden">
                            <button
                              onClick={() => updateQuantity(itemKey, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                              aria-label={`${t("quantity", language)} -`}
                              className="w-10 h-9 flex items-center justify-center text-light text-base hover:bg-bronze hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                            >
                              <MinusIcon className="w-4 h-4" />
                            </button>
                            <span className="min-w-[2.6rem] h-9 px-2 flex items-center justify-center text-light text-sm font-semibold border-x border-dark-border">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(itemKey, item.quantity + 1)}
                              aria-label={`${t("quantity", language)} +`}
                              className="w-10 h-9 flex items-center justify-center text-light text-base hover:bg-bronze hover:text-white transition-colors"
                            >
                              <PlusIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Simplified */}
        {cartItems.length > 0 && (
          <div className="border-t border-dark-border p-6 bg-dark-lighter">
            <div className="space-y-2 mb-5">
              <div className="flex justify-between items-center text-sm">
                <span className="text-light-secondary">
                  {t("subtotal", language)}
                </span>
                <span className="text-light font-medium">
                  €{subtotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-start gap-3 text-sm">
                <span className="text-light-secondary">
                  {t("shipping", language)}
                </span>
                <span className="text-light font-medium text-right max-w-[68%] leading-snug">
                  {isFreeShipping
                    ? t("freeShipping", language)
                    : shouldShowPendingShipping
                    ? t('selectCountryAtCheckout', language)
                    : (shipping === 0
                      ? t("freeShipping", language)
                      : `€${Number(shipping).toFixed(2)}`)}
                </span>
              </div>
              <div className="h-px bg-dark-border my-2" />
              <div className="flex justify-between items-center">
                <span className="text-light font-bold text-lg">
                  {t("total", language)}
                </span>
                <span className="text-gold font-bold text-2xl">
                  €{(shouldShowPendingShipping ? subtotal : total).toFixed(2)}
                </span>
              </div>
            </div>

            <Link to="/checkout" onClick={handleClose} className="block mb-3">
              <button className="w-full py-4 rounded-xl font-bold text-base bg-gradient-to-br from-bronze to-bronze-dark text-white hover:shadow-2xl hover:shadow-bronze/50 hover:-translate-y-1 active:translate-y-0 transition-all duration-300">
                {t("checkout", language)}
              </button>
            </Link>
            <button
              onClick={handleClose}
              className="w-full py-3 rounded-lg font-medium text-sm bg-dark border border-dark-border text-light hover:border-bronze hover:bg-bronze/5 transition-colors"
            >
              {t("continueShopping", language)}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
